/**
 * MiniEligibilitySimulator — Mini-simulateur en haut du panneau Super Admin
 * "Modulateurs d'éligibilité BATICO".
 *
 * Permet à l'administrateur de saisir un profil minimal (ancienneté, expérience,
 * effectif, type de marché, etc.) et de voir en temps réel le score résultant,
 * le statut (ACCEPTÉ / VALIDATION_MANUELLE / REFUS) et la cotisation ajustée,
 * pour visualiser instantanément l'effet de ses ajustements de score.
 *
 * Backend : POST /api/batico/eligibility/compute
 */
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Beaker, Play, Loader2, CheckCircle2, AlertTriangle, XOctagon, Calculator } from 'lucide-react';
import { getAuthHeader } from '../services/tokenService';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUT_CONFIG = {
  ACCEPTE: { label: 'ACCEPTÉ', icon: CheckCircle2, classes: 'bg-emerald-600 text-white border-emerald-700 shadow-sm' },
  VALIDATION_MANUELLE: { label: 'VALIDATION MANUELLE', icon: AlertTriangle, classes: 'bg-amber-500 text-white border-amber-600 shadow-sm' },
  REFUS: { label: 'REFUS', icon: XOctagon, classes: 'bg-rose-600 text-white border-rose-700 shadow-sm' },
};

// Couleurs et libellés des classes de dossier (Couche 4)
const CLASSE_CONFIG = {
  FAIBLE:              { label: 'FAIBLE',              classes: 'bg-emerald-600 text-white border-emerald-700 shadow-sm' },
  STANDARD:            { label: 'STANDARD',            classes: 'bg-sky-600 text-white border-sky-700 shadow-sm' },
  SENSIBLE:            { label: 'SENSIBLE',            classes: 'bg-amber-500 text-white border-amber-600 shadow-sm' },
  RENFORCE:            { label: 'RENFORCÉ',            classes: 'bg-orange-600 text-white border-orange-700 shadow-sm' },
  MAJORATION_PERSONNALISEE: { label: 'MAJORATION PERSONNALISÉE', classes: 'bg-violet-600 text-white border-violet-700 shadow-sm' },
  VALIDATION_MANUELLE: { label: 'VALIDATION MANUELLE', classes: 'bg-rose-600 text-white border-rose-700 shadow-sm' },
  ARBITRE:             { label: 'ARBITRÉ ⚖️',          classes: 'bg-violet-600 text-white border-violet-700 shadow-sm' },
};

const formatEur = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';
};

export const MiniEligibilitySimulator = ({ config, variant = 'annuel' }) => {
  // V36 — variant 'annuel' | 'nominatif' : détermine l'endpoint tarificateur
  // et le seuil pré-rempli. Synchronisé avec l'onglet RCDS actif (BaticoConfigV3).
  const isNominatif = variant === 'nominatif';
  const tarifEndpoint = isNominatif ? 'nominative' : 'annuel';
  const seuilEndpoint = isNominatif ? 'nominatif' : 'annuel';
  const [anc, setAnc] = useState(2);
  const [exp, setExp] = useState(5);
  const [effectif, setEffectif] = useState('1-5 salariés');
  const [typeMarche, setTypeMarche] = useState('multi_activite_limitee');
  const [caBase, setCaBase] = useState(100000);
  const [cotisBase, setCotisBase] = useState(5000);
  const [cotisBaseSynced, setCotisBaseSynced] = useState(false);
  // V36 — Indique si la cotis_base a été recalculée à partir du CA (badge "Auto-CA")
  const [cotisBaseFromCa, setCotisBaseFromCa] = useState(false);
  // True quand l'utilisateur a manuellement édité Cotis. base (désactive l'auto-calc)
  const [cotisBaseManual, setCotisBaseManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [result, setResult] = useState(null);
  // V36.56 — Override Super Admin du taux de majoration personnalisée.
  // Pré-rempli avec la valeur appliquée par le moteur (default config),
  // modifiable + bouton "Appliquer" pour recalculer.
  const [tauxMajoration, setTauxMajoration] = useState('');
  const [applyingTaux, setApplyingTaux] = useState(false);

  // Synchronisation dynamique : récupère le « Montant de base contribution
  // solidaire — RCDS {Annuel|Nominatif} » depuis l'onglet Seuils (Config BATICO V3)
  // et le pré-remplit dans le mini-simulateur.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/batico/config/v2/seuil-ca/${seuilEndpoint}`,
          { headers: getAuthHeader() },
        );
        if (aborted) return;
        const seuil = Number(
          res.data?.seuil?.seuil_minimum
          ?? res.data?.seuil_minimum
          ?? res.data?.config?.seuil_minimum
          ?? 0
        );
        if (seuil > 0) {
          setCotisBase(seuil);
          setCotisBaseSynced(true);
          setCotisBaseFromCa(false);
          setCotisBaseManual(false); // re-permettre l'auto-calc après switch d'onglet
        }
      } catch { /* silencieux : valeur par défaut conservée */ }
    })();
    return () => { aborted = true; };
  }, [seuilEndpoint]);

  // V36 — Recalcul automatique de Cotis. base via le tarificateur batico quand le CA change
  // (sauf si l'utilisateur a saisi manuellement la cotis_base).
  useEffect(() => {
    if (cotisBaseManual) return; // override manuel : on ne touche pas
    const ca = Number(caBase) || 0;
    if (ca <= 0) return;
    let aborted = false;
    const timer = setTimeout(async () => {
      try {
        setRecalcLoading(true);
        const res = await axios.post(
          `${API}/tarificateur/batico/${tarifEndpoint}`,
          {
            ca_ht: ca,
            nb_activites_go: 1, nb_activites_2nd: 0, nb_activites_pib: 0, nb_activites_tp: 0,
            type_marche: 'mono_activite',
            activites_gros_oeuvre: ['AC01'],
          },
          { headers: getAuthHeader() },
        );
        if (aborted) return;
        const computed = Number(
          res.data?.montant_ht
          ?? res.data?.cotisation_ht
          ?? res.data?.plancher_contributif?.seuil
          ?? 0
        );
        if (computed > 0) {
          setCotisBase(computed);
          setCotisBaseFromCa(true);
          setCotisBaseSynced(false);
        }
      } catch { /* silencieux, on garde la valeur courante */ }
      finally { if (!aborted) setRecalcLoading(false); }
    }, 400); // debounce 400ms
    return () => { aborted = true; clearTimeout(timer); };
  }, [caBase, cotisBaseManual, tarifEndpoint]);

  const effectifOptions = useMemo(
    () => (config?.effectif_tiers || []).map((t) => t.key || t.label).filter(Boolean),
    [config],
  );
  const typeMarcheOptions = useMemo(
    () => (config?.types_marche || []).map((t) => ({ key: t.key, label: t.label })),
    [config],
  );

  // Synchronise les valeurs par défaut avec la config chargée :
  // si la valeur initiale n'existe pas dans les options, on prend la première dispo.
  useEffect(() => {
    if (effectifOptions.length > 0 && !effectifOptions.includes(effectif)) {
      setEffectif(effectifOptions[0]);
    }
  }, [effectifOptions, effectif]);
  useEffect(() => {
    if (typeMarcheOptions.length > 0 && !typeMarcheOptions.some((t) => t.key === typeMarche)) {
      setTypeMarche(typeMarcheOptions[0].key);
    }
  }, [typeMarcheOptions, typeMarche]);

  const run = async (overrideTaux = null) => {
    const isOverride = overrideTaux !== null;
    if (isOverride) setApplyingTaux(true); else setLoading(true);
    try {
      const payload = {
        form_data: {
          anciennete_annees: Number(anc) || 0,
          experience_professionnelle_annees: Number(exp) || 0,
          effectif: effectif,
          type_marche: typeMarche,
          // Ventilations valides à 100% pour ne pas bloquer le simulateur
          territoires_ca: { metropole: Number(caBase) || 0 },
          ventilation_clientele: { particuliers: 100 },
          ventilation_contrat: { forfait: 100 },
          ventilation_travaux: { neuf: 100 },
          qualifications: [],
        },
        cotisation_base: Number(cotisBase) || 0,
        famille_rcds: 'batico',
        nombre_activites: 5,
      };
      if (isOverride) {
        payload.taux_majoration_override = Number(overrideTaux);
      }
      const res = await axios.post(
        `${API}/batico/eligibility/compute`,
        payload,
        { headers: getAuthHeader() },
      );
      if (res.data?.ok) {
        setResult(res.data);
        // Pré-remplir l'input avec le taux appliqué par le moteur
        const applied = res.data?.details?.taux_majoration_personnalisee_applique;
        if (applied != null) {
          setTauxMajoration(String(applied));
        } else if (!isOverride) {
          setTauxMajoration('');
        }
        if (isOverride) {
          toast.success(`Taux ×${Number(overrideTaux).toFixed(2)} appliqué — cotisation recalculée`);
        }
      } else {
        toast.error('Données invalides : ' + JSON.stringify(res.data?.validation_errors || res.data));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur calcul');
    } finally {
      setLoading(false);
      setApplyingTaux(false);
    }
  };

  const handleApplyTaux = () => {
    const v = parseFloat(String(tauxMajoration).replace(',', '.'));
    if (!v || v <= 0 || v > 5) {
      toast.error('Taux invalide — doit être un coefficient entre 0 et 5 (ex : 1.30)');
      return;
    }
    run(v);
  };

  const statutInfo = result && STATUT_CONFIG[result.statut];
  const StatutIcon = statutInfo?.icon;
  const classeInfo = result && CLASSE_CONFIG[result.classe_dossier];

  return (
    <Card className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 border-2 border-indigo-200 mb-4" data-testid="mini-eligibility-simulator">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Beaker className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-indigo-900">Mini-simulateur d'éligibilité</h4>
            <p className="text-[11px] text-slate-600">
              Saisis un profil-test et clique sur <strong>Calculer</strong> pour visualiser instantanément
              l'effet de tes ajustements de scores / coefs sur le statut final.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Ancienneté (ans)</Label>
            <Input type="number" step="0.5" min="0" value={anc}
              onChange={(e) => setAnc(e.target.value)}
              className="h-8 text-sm" data-testid="sim-anciennete" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Expérience dir. (ans)</Label>
            <Input type="number" step="0.5" min="0" value={exp}
              onChange={(e) => setExp(e.target.value)}
              className="h-8 text-sm" data-testid="sim-experience" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Effectif</Label>
            <select
              value={effectif}
              onChange={(e) => setEffectif(e.target.value)}
              className="h-8 text-sm w-full rounded-md border border-input bg-background px-2 text-slate-900 font-medium"
              data-testid="sim-effectif"
            >
              {effectifOptions.length === 0 && <option value="">— choisir —</option>}
              {effectifOptions.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Type de marché</Label>
            <select
              value={typeMarche}
              onChange={(e) => setTypeMarche(e.target.value)}
              className="h-8 text-sm w-full rounded-md border border-input bg-background px-2 text-slate-900 font-medium"
              data-testid="sim-type-marche"
            >
              {typeMarcheOptions.length === 0 && <option value="">— choisir —</option>}
              {typeMarcheOptions.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">CA HT (€)</Label>
            <Input type="number" step="10000" min="0" value={caBase}
              onChange={(e) => { setCaBase(e.target.value); setCotisBaseManual(false); setResult(null); }}
              className="h-8 text-sm font-mono" data-testid="sim-ca" />
            <div className="text-[9px] text-slate-500 mt-0.5 italic">
              {cotisBaseManual
                ? 'Cotis. base figée (saisie manuelle)'
                : 'Cotis. base auto-recalculée depuis le CA'}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <Label className="text-[10px] text-slate-500 uppercase">Cotis. base (€)</Label>
              {recalcLoading ? (
                <span className="text-[8px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" title="Recalcul en cours via le tarificateur">
                  <Loader2 className="h-2 w-2 animate-spin" /> CALC
                </span>
              ) : cotisBaseFromCa ? (
                <span className="text-[8px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full" title="Cotisation recalculée automatiquement à partir du CA HT saisi via le tarificateur batico">
                  ↻ AUTO-CA
                </span>
              ) : cotisBaseSynced ? (
                <span className="text-[8px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full" title={`Synchronisé avec l'onglet Seuils — Montant de base contribution solidaire RCDS ${isNominatif ? 'Nominatif' : 'Annuel'}`}>
                  ↻ SYNC
                </span>
              ) : null}
            </div>
            <Input type="number" step="100" min="0" value={cotisBase}
              onChange={(e) => { setCotisBase(e.target.value); setCotisBaseSynced(false); setCotisBaseFromCa(false); setCotisBaseManual(true); }}
              className="h-8 text-sm font-mono" data-testid="sim-cotis" />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <Button
            onClick={() => run()} disabled={loading} size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"
            data-testid="sim-run-btn"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Calculer
          </Button>
          {result && (
            <div className="flex-1 flex flex-wrap items-center gap-2" data-testid="sim-result">
              {statutInfo && (
                <Badge className={`gap-1 border ${statutInfo.classes} text-xs px-2.5 py-1 font-bold`} data-testid="sim-result-statut">
                  {StatutIcon && <StatutIcon className="h-3.5 w-3.5" />}
                  {statutInfo.label}
                </Badge>
              )}
              <Badge className="bg-slate-800 text-white border-slate-900 text-xs gap-1 font-mono px-2.5 py-1 shadow-sm font-semibold">
                Score : <strong className="text-white">{result.score_eligibilite}</strong>
              </Badge>
              {classeInfo && (
                <Badge
                  className={`gap-1 border ${classeInfo.classes} text-xs px-2.5 py-1 font-bold`}
                  data-testid="sim-result-classe"
                >
                  Classe : {classeInfo.label}
                  {result.coefficient_dossier != null && (
                    <span className="ml-1 font-mono opacity-80">
                      × {Number(result.coefficient_dossier).toFixed(2)}
                    </span>
                  )}
                </Badge>
              )}
              <Badge className="bg-slate-800 text-white border-slate-900 text-xs gap-1 font-mono px-2.5 py-1 shadow-sm font-semibold">
                <Calculator className="h-3 w-3" />
                Cotisation ajustée : <strong className="text-white">{formatEur(result.cotisation_finale)}</strong>
              </Badge>
              {result.coef_multiplicatif !== undefined && (
                <Badge className="bg-slate-700 text-white border-slate-800 text-xs font-mono px-2.5 py-1 shadow-sm font-semibold">
                  Coef × {Number(result.coef_multiplicatif).toFixed(3)}
                </Badge>
              )}
              {result.modulateur_additif_pct !== undefined && Number(result.modulateur_additif_pct) !== 0 && (
                <Badge className="bg-slate-600 text-white border-slate-700 text-xs font-mono px-2.5 py-1 shadow-sm font-semibold">
                  + {Number(result.modulateur_additif_pct).toFixed(1)}%
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* V36.56 — Bloc majoration personnalisée : visible quand le moteur a
            appliqué une majoration (classe MAJORATION_PERSONNALISEE). Permet
            au Super Admin d'override le taux en un clic. */}
        {result?.classe_dossier === 'MAJORATION_PERSONNALISEE' && (
          <div
            className="mt-3 p-3 rounded-md border-2 border-violet-300 bg-gradient-to-r from-violet-50 via-white to-purple-50"
            data-testid="sim-majoration-block"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-violet-100 flex items-center justify-center">
                <Calculator className="h-4 w-4 text-violet-700" />
              </div>
              <div className="flex-1">
                <h5 className="text-xs font-bold text-violet-900">Taux de majoration personnalisé</h5>
                <p className="text-[10px] text-slate-600 leading-tight">
                  Le score dépasse la dernière classe. Un taux par défaut est appliqué
                  automatiquement — vous pouvez le surcharger ci-dessous.
                </p>
              </div>
              {result?.details?.taux_majoration_personnalisee_override && (
                <span className="text-[9px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                  OVERRIDE ACTIF
                </span>
              )}
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 max-w-[160px]">
                <Label className="text-[10px] text-slate-500 uppercase">Taux ×</Label>
                <Input
                  type="number" step="0.05" min="0.5" max="5"
                  value={tauxMajoration}
                  onChange={(e) => setTauxMajoration(e.target.value)}
                  className="h-8 text-sm font-mono"
                  placeholder="1.30"
                  data-testid="sim-taux-majoration-input"
                />
              </div>
              <Button
                onClick={handleApplyTaux}
                disabled={applyingTaux || !tauxMajoration}
                size="sm"
                className="h-8 bg-violet-600 hover:bg-violet-700 gap-1.5"
                data-testid="sim-apply-taux-btn"
              >
                {applyingTaux ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calculator className="h-3.5 w-3.5" />}
                Appliquer
              </Button>
              <div className="text-[10px] text-slate-500 italic">
                Défaut config : ×{Number(result?.details?.taux_majoration_personnalisee_default ?? 1.30).toFixed(2)}
              </div>
            </div>

            {/* V36.57 — Prévisualisation temps réel de l'écart de cotisation
                vs "cotisation standard" (sans majoration). Permet d'arbitrer
                la valeur du taux AVANT de cliquer Appliquer. */}
            {(() => {
              const cotBaseModulee = Number(result?.cotisation_apres_modulateurs || 0);
              const appliedTaux = Number(result?.details?.taux_majoration_personnalisee_applique ?? result?.coefficient_dossier ?? 1);
              const previewTaux = parseFloat(String(tauxMajoration).replace(',', '.'));
              if (!cotBaseModulee || !previewTaux || previewTaux <= 0) return null;
              const previewCotisation = cotBaseModulee * previewTaux;
              const cotisationStandard = cotBaseModulee; // taux x1.0 = standard
              const delta = previewCotisation - cotisationStandard;
              const isOverride = Math.abs(previewTaux - appliedTaux) > 0.001;
              return (
                <div
                  className="mt-2 grid grid-cols-3 gap-2 text-[11px]"
                  data-testid="sim-preview-delta"
                >
                  <div className="rounded-md bg-white/70 border border-violet-200 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Cotisation standard</div>
                    <div className="font-mono font-bold text-slate-700">{formatEur(cotisationStandard)}</div>
                  </div>
                  <div className="rounded-md bg-white/70 border border-violet-300 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-violet-600 font-semibold flex items-center gap-1">
                      Cotisation prévisualisée
                      {isOverride && (
                        <span className="text-[8px] bg-violet-100 text-violet-700 px-1 py-0 rounded">PREVIEW</span>
                      )}
                    </div>
                    <div className="font-mono font-bold text-violet-700" data-testid="sim-preview-cotisation">{formatEur(previewCotisation)}</div>
                  </div>
                  <div className={`rounded-md px-2 py-1.5 border ${delta >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`}>
                    <div className={`text-[9px] uppercase tracking-wider font-semibold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      Écart vs standard
                    </div>
                    <div className={`font-mono font-bold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="sim-preview-delta-value">
                      {delta >= 0 ? '+' : ''}{formatEur(delta)}
                      <span className="text-[10px] font-normal ml-1 opacity-70">
                        ({delta >= 0 ? '+' : ''}{((previewTaux - 1) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {result?.alertes?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {result.alertes.slice(0, 4).map((a, i) => (
              <span key={i} className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                {a}
              </span>
            ))}
          </div>
        )}

        {/* V36 — Décomposition pédagogique du calcul */}
        {result && (
          <div className="mt-3 rounded-md border border-slate-700 bg-slate-900/95 text-slate-100 overflow-hidden" data-testid="sim-decomposition">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-300 bg-slate-800/80 border-b border-slate-700 flex items-center gap-1.5">
              <Calculator className="h-3 w-3" />
              Décomposition du calcul
            </div>
            <table className="w-full text-[11px] font-mono">
              <tbody>
                <tr className="border-b border-slate-800">
                  <td className="px-3 py-1.5 text-slate-400">Cotisation de base</td>
                  <td className="px-3 py-1.5 text-right text-slate-100 font-semibold">{formatEur(result.cotisation_base)}</td>
                </tr>
                {result.coef_multiplicatif !== undefined && (
                  <tr className="border-b border-slate-800">
                    <td className="px-3 py-1.5 text-slate-400">× Coef multiplicatif <span className="text-[9px] text-slate-500">(exp · anc · effectif · qualif · territoire · marché)</span></td>
                    <td className="px-3 py-1.5 text-right text-amber-300 font-semibold">× {Number(result.coef_multiplicatif).toFixed(3)}</td>
                  </tr>
                )}
                {result.modulateur_additif_pct !== undefined && Number(result.modulateur_additif_pct) !== 0 && (
                  <tr className="border-b border-slate-800">
                    <td className="px-3 py-1.5 text-slate-400">+ Modulateurs additifs <span className="text-[9px] text-slate-500">(clientèle + contrat + travaux)</span></td>
                    <td className="px-3 py-1.5 text-right text-amber-300 font-semibold">+ {Number(result.modulateur_additif_pct).toFixed(2)} %</td>
                  </tr>
                )}
                {result.score_tarif_pct !== undefined && Number(result.score_tarif_pct) !== 0 && (
                  <tr className="border-b border-slate-800">
                    <td className="px-3 py-1.5 text-slate-400">+ Impact score global <span className="text-[9px] text-slate-500">(score {result.score_global ?? 0} / diviseur)</span></td>
                    <td className="px-3 py-1.5 text-right text-amber-300 font-semibold">{Number(result.score_tarif_pct) >= 0 ? '+' : ''}{Number(result.score_tarif_pct).toFixed(2)} %</td>
                  </tr>
                )}
                {result.coefficient_dossier != null && (
                  <tr className="border-b border-slate-800">
                    <td className="px-3 py-1.5 text-slate-400">× Coefficient classe dossier <span className="text-[9px] text-slate-500">({result.classe_dossier})</span></td>
                    <td className="px-3 py-1.5 text-right text-amber-300 font-semibold">× {Number(result.coefficient_dossier).toFixed(2)}</td>
                  </tr>
                )}
                <tr className="bg-slate-800/60">
                  <td className="px-3 py-2 font-bold text-slate-100">= Cotisation finale ajustée</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-400 text-sm">{formatEur(result.cotisation_finale)}</td>
                </tr>
              </tbody>
            </table>
            {/* Formule courte rappel */}
            <div className="px-3 py-1.5 text-[9px] text-slate-500 bg-slate-950/60 border-t border-slate-800 text-center font-mono leading-tight">
              cotisation_base × coef_multiplicatif × (1 + modulateur_additif_pct/100) × (1 + score_tarif_pct/100)
              {result.coefficient_dossier != null && ' × coefficient_classe'}
            </div>
          </div>
        )}

        {result?.raisons_blocage?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {result.raisons_blocage.slice(0, 4).map((a, i) => (
              <span key={i} className="text-[10px] bg-rose-50 text-rose-800 border border-rose-200 rounded px-1.5 py-0.5">
                ⛔ {a}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MiniEligibilitySimulator;
